import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Alert, AlertDescription } from './ui/alert';
import { PK_COLORS } from './PathkitLogo';
import pkLogo from '../imports/PK.svg';
import { projectId } from '../utils/supabase/info';
import { createSession, getCurrentSubdomain, storeSessionLocally, clearSessionFromLocalStorage, getRootDomain } from '../utils/sessionManager';
import { isSystemAdmin, isActiveViewSystemAdmin } from '../utils/roleUtils';
import { ArrowRight } from 'lucide-react';

import { bearerAuthHeader } from '../utils/apiHeaders';
import { createHandoffCode } from '../utils/sessionHandoff';
interface LoginProps {
  onLogin: (sessionId: string, user: any, company: any) => void;
}

export function Login({ onLogin }: LoginProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'email' | 'password'>('email');
  
  // Detect if we're on root domain (for System Admins) or subdomain (for CF Admins/Consultants)
  const hostname = window.location.hostname;
  const isRootDomain = hostname === 'pathkit.pro' || hostname === 'www.pathkit.pro' || 
                       hostname === 'admin.pathkit.pro' || // System Admin dedicated domain
                       hostname === 'staging.pathkit.pro' || // System Admins can login to staging
                       hostname === 'localhost' ||
                       hostname.includes('figmaiframepreview');
  const subdomain = !isRootDomain ? hostname.split('.')[0] : null;

  // Pre-fill email from sessionStorage (for redirected users).
  // The password field takes focus through its autoFocus when the password step mounts; a delayed
  // focus() call could fire after the component (or a test's document) was gone.
  useEffect(() => {
    const storedEmail = sessionStorage.getItem('prefill_email');
    if (storedEmail) {
      setEmail(storedEmail);
      setStep('password');
      sessionStorage.removeItem('prefill_email');
    }
  }, []);

  const handleEmailContinue = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setError('');
    setStep('password');
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/login`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...bearerAuthHeader(),
          },
          body: JSON.stringify({ email, password }),
        }
      );

      const text = await response.text();
      
      // Check if response is HTML (server error)
      if (text.startsWith('<') || text.startsWith('<!')) {
        setError('Server error. Please try again or contact support.');
        setLoading(false);
        return;
      }

      let data;
      try {
        data = JSON.parse(text);
      } catch (parseError) {
        console.error('Failed to parse response as JSON:', text);
        setError('Invalid server response. Please try again or contact support.');
        setLoading(false);
        return;
      }

      if (!response.ok) {
        console.error('Login failed with status:', response.status, 'Response:', data);
        setError(data.error || 'Invalid credentials. Please try again.');
        setLoading(false);
        return;
      }

      // PATHKIT SESSION TOKEN: Read from sessionToken field (NOT accessToken)
      if (!data.sessionToken || !data.user) {
        console.error('Login response missing required data. Received:', data);
        setError('Invalid login response. Please try again or contact support.');
        setLoading(false);
        return;
      }

      // FAIL-SAFE: Validate PathKit session token format (MUST start with sess_ or imp_)
      const sessionToken = data.sessionToken;
      
      // Validate token is a valid string
      if (!sessionToken || typeof sessionToken !== 'string') {
        console.error('[LOGIN] ❌ Invalid session token (not a string):', typeof sessionToken);
        setError('Login failed: Invalid session token received from server.');
        setLoading(false);
        return;
      }
      
      // Validate token length
      if (sessionToken.length < 20) {
        console.error('[LOGIN] ❌ Invalid session token (too short):', sessionToken.length);
        setError('Login failed: Invalid session token format.');
        setLoading(false);
        return;
      }
      
      // CRITICAL: Validate token starts with sess_ or imp_ (PathKit format, not Supabase JWT)
      if (!sessionToken.startsWith('sess_') && !sessionToken.startsWith('imp_')) {
        console.error('[LOGIN] ❌ Invalid session token format. Expected sess_* or imp_*, got:', sessionToken.substring(0, 10) + '...');
        setError('Login failed: Invalid session token format received from server.');
        setLoading(false);
        return;
      }
      
      console.log('[LOGIN] ✅ PathKit session token validated (prefix:', sessionToken.substring(0, 5), ', length:', sessionToken.length, ')');
      
      // Check subdomain requirements (system admin can login from root).
      //
      // AUTHORIZATION uses the shared isSystemAdmin() helper, which honours the
      // base-privilege flag (isAdmin) that survives a role switch. A system admin
      // who is currently "testing as" cfAdmin has role === 'cfAdmin' but
      // isAdmin === true; checking data.user.role alone threw away a successful
      // login on root hosts (localhost, staging.pathkit.pro) with a misleading
      // "login from your subdomain" error. See docs/LOCAL_LOGIN_TROUBLESHOOTING.md.
      const currentSubdomain = getCurrentSubdomain();
      const hasSystemAdminPrivilege =
        isSystemAdmin(data.user) || data.user.role === 'system_admin';
      // VIEW SELECTION: only an *active* system admin is bounced from a tenant
      // subdomain to the root domain; a role-switched admin stays on the tenant.
      const isActiveSystemAdmin = isActiveViewSystemAdmin(data.user);
      
      if (!currentSubdomain && !hasSystemAdminPrivilege) {
        console.error('[LOGIN] Cannot create session: not on a subdomain');
        const isLocalDev = hostname === 'localhost' || hostname === '127.0.0.1';
        setError(
          isLocalDev
            ? 'Please login from your consulting firm\'s subdomain. On localhost, add ?tenant=<firm-subdomain> to the URL (e.g., http://localhost:5173/login?tenant=yourfirm).'
            : 'Please login from your consulting firm\'s subdomain (e.g., yourfirm.pathkit.pro)'
        );
        setLoading(false);
        return;
      }
      
      // If System Admin on subdomain, redirect to root domain
      if (isActiveSystemAdmin && subdomain && subdomain !== 'www') {
        const rootDomain = getRootDomain();
        console.log('[LOGIN] ⚠️ System Admin detected on subdomain, redirecting to root domain...');
        
        clearSessionFromLocalStorage();
        
        const protocol = window.location.protocol;
        // One-time handoff code instead of the session token in the URL.
        const code = await createHandoffCode(sessionToken);
        window.location.href = `${protocol}//${rootDomain}/system-admin?handoff=${encodeURIComponent(code)}`;
        return;
      }
      
      // ENTERPRISE AUTH: Store session using canonical helper (prevents undefined tokens)
      storeSessionLocally(sessionToken, data.user, data.company);
      
      console.log('[LOGIN] ✅ Session created successfully');

      // Pass sessionToken to parent (AppRouter expects sessionId as first param)
      onLogin(sessionToken, data.user, data.company);
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.message || 'Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep('email');
    setPassword('');
    setError('');
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 py-12 bg-[#F8F9FB]">
      {/* Brand Header */}
      <div className="flex flex-col items-center mb-12">
        <img src={pkLogo} alt="PathKit" className="w-12 h-12 mb-4" />
        <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">
          PathKit
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Enterprise Consulting Platform
        </p>
      </div>

      {/* Login Card */}
      <div className="w-full max-w-[440px] bg-white rounded-2xl border border-gray-200 shadow-sm p-8">
        {step === 'email' ? (
          <>
            <div className="mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                Sign in to PathKit
              </h2>
              <p className="text-sm text-gray-500">
                Enter your work email to continue
              </p>
            </div>

            {error && (
              <Alert variant="destructive" className="mb-6">
                <AlertDescription className="text-sm">
                  {error}
                </AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleEmailContinue} className="space-y-4">
              <div>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  className="h-11 text-[15px]"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full h-11 text-[15px] font-medium"
                style={{ backgroundColor: PK_COLORS.teal }}
              >
                Continue
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </form>
          </>
        ) : (
          <>
            <div className="mb-6">
              <button
                onClick={handleBack}
                className="text-sm text-gray-500 hover:text-gray-700 mb-4 flex items-center"
              >
                ← Back
              </button>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                Enter your password
              </h2>
              <p className="text-sm text-gray-500">
                {email}
              </p>
            </div>

            {error && (
              <Alert variant="destructive" className="mb-6">
                <AlertDescription className="text-sm">
                  {error}
                </AlertDescription>
              </Alert>
            )}

            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  autoFocus
                  className="h-11 text-[15px]"
                />
                <div className="mt-2 text-right">
                  <a href="/forgot-password" className="text-sm text-gray-600 hover:text-gray-900">
                    Forgot password?
                  </a>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full h-11 text-[15px] font-medium"
                disabled={loading}
                style={{ backgroundColor: PK_COLORS.teal }}
              >
                {loading ? 'Signing in...' : 'Sign in'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </form>
          </>
        )}
      </div>

      {/* Secondary Help Links */}
      <div className="mt-8 text-center">
        <p className="text-sm text-gray-500 mb-2">
          Need help signing in?
        </p>
        <a 
          href="/forgot-password" 
          className="text-sm text-gray-600 hover:text-gray-900 font-medium"
        >
          Forgot password?
        </a>
      </div>

      {/* System Admin Footer Link */}
      <div className="mt-16">
        <a 
          href="https://admin.pathkit.pro" 
          className="text-xs text-gray-400 hover:text-gray-500"
        >
          System Admin
        </a>
      </div>
    </div>
  );
}